import React from "react";
import { useAuth, useCustomer, useExcursion, useLeave } from "contexts/api";
import { usePermissions, Permission } from "contexts/app/permissions";
import useAppSettings from "contexts/app/appSettings";
import { useConfirmationModal } from "contexts/app/confirmation";
import { useAlert } from "contexts/app/notificationToast";
import type {
  Excursion,
  ExcursionStatus,
  ExcursionRequest,
  ExcursionGroupRequest,
  ExcursionCustomer,
  ExcursionUser,
  ExcursionBooking,
} from "api/excursions/Excursion.typed";
import {
  ExcursionStatusPending,
  ExcursionStatusDone,
  ExcursionStatusCancelled,
} from "api/excursions/Excursion.typed";
import type { LeaveRequest } from "api/leave/Leave.typed";
import type { UserWithRole } from "api/auth/Auth.typed";
import type { MultiComment } from "api/zekay/comments";

import { EXCURSION_DEFAULT_COLOR, GROUP_DEFAULT_COLOR } from "utils/colorPalette";
import { userDisplayName } from "utils/userHelper";
import type { ZekayCustomer } from "api/customers/Customer.typed";
import type { SearchResultItem } from "components/common/SearchInputWithDropdown";
import {
  filterTemplateToRecord,
  firstLastQualificationLabel,
  isUserGuideForExcursionParams,
  personName,
  sanitizeExcursionParamsAgainstTemplate,
  stripPicklistSearchMetadataForPersist,
  toDateInputValue,
} from "../../../excursions/shared/utils/excursionUtils";
import type { ExcursionGroupCardClient } from "../../../excursions/shared/utils/excursionPlanningTypes";
import { generateExcursionPdf } from "../../../excursions/shared/utils/excursionPdfExport";
import type {
  PdfGroupData,
  PdfExcursionData,
} from "../../../excursions/shared/utils/excursionPdfExport";
import { useOptionalExcursionTemplates } from "contexts/app/excursionsData";
import { extractDataFromResponse } from "utils/paginationHelpers";
import {
  findUserSlotConflict,
  monthRangeContainingYmd,
  parseExcursionSlotBounds,
  slotConflictHint,
} from "../../../excursions/shared/utils/excursionSlotUserConflict";
import { normalizeCustomerEmoji } from "../../../common/customerEmoji";
import { formatInProgressTrainingsSessionsTooltip } from "../../../excursions/shared/utils/excursionInProgressTrainingsTooltip";
import { collectExcursionCounterValidationGroupSlices } from "../../../excursions/shared/utils/excursionUtils";
import { useCustomersModulePermissions } from "../../../customers/hooks/useCustomersModulePermissions";
import { normalizeToList } from "../model/arrayUtils";
import {
  firstImportantComment,
  groupCustomersList,
  mergeBuiltGroupsWithPrev,
  type GroupWithLabels,
} from "../model/groupModel";
import { mapApiExcursionGroupsToFormState } from "../model/mapExcursionGroupsToForm";
import { useNextGroupLocalKey } from "./useNextGroupLocalKey";
import type { ExcursionModalProps } from "../excursionModalTypes";

const maxExcursionCancellationReasonLen = 4000;

export function useExcursionModalOrchestration(props: ExcursionModalProps) {
  const {
    open,
    onClose,
    initialExcursion,
    preloadedCustomers,
    preloadedUsers,
    onValidateCountersApplied,
    onPersistAfterCounters,
  } = props;

  const isReadonly = props.readonly === true;
  const onSave = isReadonly ? undefined : props.onSave;
  const onDelete = isReadonly ? undefined : props.onDelete;
  const initialDate = props.initialDate ?? initialExcursion?.date ?? "";
  const initialStartTime = props.initialStartTime;
  const initialEndTime = props.initialEndTime;
  const canEdit = isReadonly ? false : (props.canEdit ?? false);
  const nextGroupLocalKey = useNextGroupLocalKey();

  const { searchCustomers, getCustomersBatch } = useCustomer();
  const { groupTemplates: sharedGroupTemplates, excursionTemplates: sharedExcursionTemplates } =
    useOptionalExcursionTemplates();
  const { config } = useAppSettings();
  const isDesync = config?.CustomersSync === false;
  const { getUsers, user, userCompanies } = useAuth();
  const {
    listExcursionParams,
    listExcursionGroupParams,
    updateExcursionGroupComments,
    listExcursionBookings,
  } = useExcursion();
  const { getLeaveRequests } = useLeave();
  const { showConfirmation } = useConfirmationModal();
  const { showAlert } = useAlert();
  const { hasPermission } = usePermissions();

  const customerOverviewModules = useCustomersModulePermissions(isDesync);
  const canOpenCustomerOverview =
    customerOverviewModules.canOpenProfile ||
    customerOverviewModules.canOpenExcursionsModal ||
    customerOverviewModules.canOpenCustomerProducts ||
    customerOverviewModules.canOpenTrainingsModal;

  const [date, setDate] = React.useState(initialDate);
  const [name, setName] = React.useState(initialExcursion?.name ?? "");
  const [startTime, setStartTime] = React.useState(
    initialExcursion?.start_time ?? initialStartTime ?? "",
  );
  const [endTime, setEndTime] = React.useState(initialExcursion?.end_time ?? initialEndTime ?? "");
  const [excursionColor, setExcursionColor] = React.useState(
    initialExcursion?.color ?? EXCURSION_DEFAULT_COLOR,
  );
  const [status, setStatus] = React.useState<ExcursionStatus>(
    initialExcursion?.status ?? ExcursionStatusPending,
  );
  const [cancellationReason, setCancellationReason] = React.useState(() =>
    (initialExcursion?.cancellation_reason ?? "").trim(),
  );
  const [groups, setGroups] = React.useState<GroupWithLabels[]>(() => []);
  const [excursionTemplatesList, setExcursionTemplatesList] = React.useState<
    {
      id?: number;
      name?: string;
      color?: string;
      params_template?: Record<string, unknown>;
      should_be_used_by_default?: boolean;
      max_people_per_excursion?: number;
      max_groups_per_excursion?: number;
    }[]
  >([]);
  const [groupTemplatesList, setGroupTemplatesList] = React.useState<
    {
      id?: number;
      name?: string;
      params_template?: Record<string, unknown>;
      should_be_used_by_default?: boolean;
      max_users_per_group?: number;
      max_customers_per_group?: number;
      color?: string;
    }[]
  >([]);
  const [selectedExcursionTemplateId, setSelectedExcursionTemplateId] = React.useState<
    number | null
  >(null);
  const [params, setParams] = React.useState<Record<string, unknown>>({});
  const [paramsTemplate, setParamsTemplate] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(false);
  const [dataLoading, setDataLoading] = React.useState(false);
  const [users, setUsers] = React.useState<
    { id: number; name: string; first_name?: string; last_name?: string; qualification?: string }[]
  >([]);
  const [customersForLabels, setCustomersForLabels] = React.useState<ZekayCustomer[]>(
    preloadedCustomers ?? [],
  );
  const [clientSearch, setClientSearch] = React.useState<{
    groupIndex: number;
    query: string;
    results: ZekayCustomer[];
  } | null>(null);
  const [userSearch, setUserSearch] = React.useState<{ groupIndex: number; query: string } | null>(
    null,
  );
  const [commentsModalGroupIndex, setCommentsModalGroupIndex] = React.useState<number | null>(null);
  const [createCustomerModalGroupIndex, setCreateCustomerModalGroupIndex] = React.useState<
    number | null
  >(null);
  const [createCustomerDraft, setCreateCustomerDraft] = React.useState<ZekayCustomer | undefined>();
  const [bookings, setBookings] = React.useState<ExcursionBooking[]>([]);
  const [slotOverlapLeaves, setSlotOverlapLeaves] = React.useState<LeaveRequest[]>([]);
  const [slotOverlapBookings, setSlotOverlapBookings] = React.useState<ExcursionBooking[]>([]);
  const [scrollToGroupLocalKey, setScrollToGroupLocalKey] = React.useState<string | null>(null);
  const [validateCountersOpen, setValidateCountersOpen] = React.useState(false);
  const [customerOverviewCustomerId, setCustomerOverviewCustomerId] = React.useState<number | null>(
    null,
  );

  const counterValidationGroupSlices = React.useMemo(
    () =>
      collectExcursionCounterValidationGroupSlices(
        groups.map((g) => ({
          customers: groupCustomersList(g),
          users: g.users ?? [],
        })),
      ),
    [groups],
  );
  const counterValidationHasClients = counterValidationGroupSlices.length > 0;

  const handleHeaderStatusChange = React.useCallback(
    (s: ExcursionStatus) => {
      if (
        s === ExcursionStatusDone &&
        hasPermission(Permission.BILLING_COUNTERS_EDIT) &&
        counterValidationHasClients
      ) {
        setValidateCountersOpen(true);
        return;
      }
      setStatus(s);
      if (s !== ExcursionStatusCancelled) {
        setCancellationReason("");
      }
    },
    [hasPermission, counterValidationHasClients],
  );

  const currentUserName = user ? userDisplayName(user) : undefined;

  const lastSyncedInitialRef = React.useRef<Excursion | null>(null);
  const formExcursionSyncKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!open) {
      lastSyncedInitialRef.current = null;
      formExcursionSyncKeyRef.current = null;
      setScrollToGroupLocalKey(null);
      setCustomerOverviewCustomerId(null);
      return;
    }
    if (initialExcursion && lastSyncedInitialRef.current === initialExcursion) {
      return;
    }
    lastSyncedInitialRef.current = initialExcursion ?? null;
    const syncKey =
      initialExcursion?.id != null ? `id:${initialExcursion.id}` : `new:${initialDate}`;
    const isSameExcursionResync = formExcursionSyncKeyRef.current === syncKey;
    if (!isSameExcursionResync) {
      formExcursionSyncKeyRef.current = syncKey;
    }
    setDate(initialDate);
    setName(initialExcursion?.name ?? "");
    const start = initialExcursion?.start_time ?? initialStartTime ?? "";
    const end = initialExcursion?.end_time ?? initialEndTime ?? "";
    setStartTime(start);
    setEndTime(end);
    setExcursionColor(initialExcursion?.color ?? EXCURSION_DEFAULT_COLOR);
    const nextStatus = initialExcursion?.status ?? ExcursionStatusPending;
    setStatus(nextStatus);
    setCancellationReason((initialExcursion?.cancellation_reason ?? "").trim());
    // Params shape is resolved later once the templates list is fetched (see effect below).
    // Here we just keep the raw params; sanitization happens after `paramsTemplate` is set.
    setParams((initialExcursion?.params as Record<string, unknown>) ?? {});
    const nextGroups = mapApiExcursionGroupsToFormState(
      initialExcursion?.groups,
      nextGroupLocalKey,
    );
    if (isSameExcursionResync) {
      setGroups((prev) => mergeBuiltGroupsWithPrev(prev, nextGroups));
    } else {
      setGroups(nextGroups);
    }
    setClientSearch(null);
    setUserSearch(null);
    setCommentsModalGroupIndex(null);
  }, [open, initialDate, initialExcursion, initialStartTime, initialEndTime, nextGroupLocalKey]);

  React.useEffect(() => {
    if (!open || isReadonly) return;

    setDataLoading(true);
    if (sharedExcursionTemplates.length > 0) setExcursionTemplatesList(sharedExcursionTemplates);
    if (sharedGroupTemplates.length > 0) setGroupTemplatesList(sharedGroupTemplates);
    const excursionId = initialExcursion?.id;
    Promise.all([
      listExcursionParams() ?? [],
      listExcursionGroupParams() ?? [],
      getUsers(),
      excursionId ? listExcursionBookings({ excursion_id: excursionId }) : Promise.resolve([]),
    ])
      .then(([excList, grpList, userList, bookingsList]) => {
        setBookings(bookingsList ?? []);
        const exc = normalizeToList(excList);
        const grp = normalizeToList(grpList);
        const mergedExc = exc.length > 0 ? exc : sharedExcursionTemplates;
        const mergedGrp = grp.length > 0 ? grp : sharedGroupTemplates;
        setExcursionTemplatesList(mergedExc);
        setGroupTemplatesList(mergedGrp);
        const defaultExc = mergedExc.find((t) => t.should_be_used_by_default) ?? mergedExc[0];
        if (defaultExc != null) {
          setSelectedExcursionTemplateId(defaultExc.id ?? null);
          setParamsTemplate(
            filterTemplateToRecord(defaultExc.params_template as Record<string, unknown>),
          );
          if (initialExcursion?.id == null) {
            const fromTemplate = typeof defaultExc.name === "string" ? defaultExc.name.trim() : "";
            if (fromTemplate !== "") setName((prev) => (prev.trim() !== "" ? prev : fromTemplate));
            const fromColor = typeof defaultExc.color === "string" ? defaultExc.color.trim() : "";
            if (fromColor !== "") {
              setExcursionColor((prev) => {
                const current = (prev ?? "").trim();
                return current === "" || current === EXCURSION_DEFAULT_COLOR ? fromColor : prev;
              });
            }
          }
        }
        const us = userList ?? [];
        setUsers(
          us.map((u: UserWithRole) => ({
            id: u.user.id,
            name: userDisplayName(u.user) || `Utilisateur #${u.user.id}`,
            first_name: u.user.first_name?.trim() || undefined,
            last_name: u.user.last_name?.trim() || undefined,
            qualification: u.user.last_qualification?.trim() || undefined,
            phone_number: u.user.phone_number?.trim() || undefined,
          })),
        );
      })
      .finally(() => setDataLoading(false));
  }, [
    open,
    isReadonly,
    listExcursionParams,
    listExcursionGroupParams,
    getUsers,
    listExcursionBookings,
    initialExcursion?.id,
    sharedExcursionTemplates,
    sharedGroupTemplates,
  ]);

  React.useEffect(() => {
    if (!open || !isReadonly) return;

    setExcursionTemplatesList(sharedExcursionTemplates);
    setGroupTemplatesList(sharedGroupTemplates);
    const defaultExc =
      sharedExcursionTemplates.find((t) => t.should_be_used_by_default) ??
      sharedExcursionTemplates[0];
    if (defaultExc != null) {
      setSelectedExcursionTemplateId(defaultExc.id ?? null);
      setParamsTemplate(
        filterTemplateToRecord(defaultExc.params_template as Record<string, unknown>),
      );
    }
  }, [open, isReadonly, sharedExcursionTemplates, sharedGroupTemplates]);

  React.useEffect(() => {
    if (!open || !isReadonly) return;

    if (preloadedUsers?.length) {
      setUsers(preloadedUsers);

      return;
    }

    let cancelled = false;

    setDataLoading(true);
    getUsers()
      .then((userList) => {
        if (cancelled) return;
        const us = userList ?? [];
        setUsers(
          us.map((u: UserWithRole) => ({
            id: u.user.id,
            name: userDisplayName(u.user) || `Utilisateur #${u.user.id}`,
            first_name: u.user.first_name?.trim() || undefined,
            last_name: u.user.last_name?.trim() || undefined,
            qualification: u.user.last_qualification?.trim() || undefined,
            phone_number: u.user.phone_number?.trim() || undefined,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setDataLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isReadonly, preloadedUsers, getUsers]);

  React.useEffect(() => {
    if (!open || initialExcursion == null) return;
    const templateId = initialExcursion.params_template_id;
    if (templateId == null) return;
    const t = excursionTemplatesList.find((e) => e.id === templateId);
    if (t == null) return;
    setSelectedExcursionTemplateId(templateId);
    setParamsTemplate(filterTemplateToRecord(t.params_template as Record<string, unknown>));
  }, [open, initialExcursion, initialExcursion?.params_template_id, excursionTemplatesList]);

  React.useEffect(() => {
    if (!open) return;
    if (Object.keys(paramsTemplate).length === 0) return;
    setParams((prev) => sanitizeExcursionParamsAgainstTemplate(paramsTemplate, prev));
  }, [open, paramsTemplate, initialExcursion?.id]);

  React.useLayoutEffect(() => {
    if (!scrollToGroupLocalKey || !open) return;
    const el = document.getElementById(`zekay-excursion-group-${scrollToGroupLocalKey}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      el.focus({ preventScroll: true });
    }
    setScrollToGroupLocalKey(null);
  }, [scrollToGroupLocalKey, open]);

  const handleExcursionTemplateChange = React.useCallback(
    (templateId: number | null) => {
      setSelectedExcursionTemplateId(templateId);
      if (templateId == null) {
        setParamsTemplate({});
        setParams({});
        return;
      }
      const t = excursionTemplatesList.find((e) => e.id === templateId);
      if (t) {
        setParamsTemplate(filterTemplateToRecord(t.params_template as Record<string, unknown>));
        setParams({});
        const templateName = typeof t.name === "string" ? t.name.trim() : "";
        if (templateName !== "") {
          // Preserve user-customized titles; only sync when empty or matching a known template name.
          setName((prev) => {
            const trimmed = prev.trim();
            if (trimmed === "") return templateName;
            const matchesKnownTemplate = excursionTemplatesList.some(
              (e) => typeof e.name === "string" && e.name.trim() === trimmed,
            );
            return matchesKnownTemplate ? templateName : prev;
          });
        }
        const templateColor = typeof t.color === "string" ? t.color.trim() : "";
        if (templateColor !== "") {
          // Preserve user-customized colors; only sync when default or matching a known template color.
          setExcursionColor((prev) => {
            const current = (prev ?? "").trim();
            if (current === "" || current === EXCURSION_DEFAULT_COLOR) return templateColor;
            const matchesKnownTemplate = excursionTemplatesList.some(
              (e) => typeof e.color === "string" && e.color.trim() === current,
            );
            return matchesKnownTemplate ? templateColor : prev;
          });
        }
      }
    },
    [excursionTemplatesList],
  );

  const defaultGroupTemplateId =
    groupTemplatesList.find((t) => t.should_be_used_by_default)?.id ??
    groupTemplatesList[0]?.id ??
    null;

  React.useEffect(() => {
    if (!open || groupTemplatesList.length === 0) return;
    setGroups((prev) =>
      prev.map((g) => {
        const templateId = g.paramsTemplateId ?? defaultGroupTemplateId;
        const t =
          templateId != null
            ? groupTemplatesList.find((e) => e.id === templateId)
            : groupTemplatesList[0];
        const tpl = filterTemplateToRecord(t?.params_template as Record<string, unknown>);
        if (Object.keys(tpl).length === 0) return g;
        return {
          ...g,
          params: sanitizeExcursionParamsAgainstTemplate(
            tpl,
            (g.params as Record<string, unknown>) ?? {},
          ),
        };
      }),
    );
  }, [open, groupTemplatesList, defaultGroupTemplateId]);

  const selectedExcursionTemplate =
    selectedExcursionTemplateId != null
      ? (excursionTemplatesList.find((t) => t.id === selectedExcursionTemplateId) ??
        excursionTemplatesList[0])
      : excursionTemplatesList[0];

  const modalTotals = React.useMemo(() => {
    const nGroups = groups.length;
    const leadCount = groups.reduce((s, g) => s + (g.users?.length ?? 0), 0);
    const peopleCount = groups.reduce((s, g) => s + groupCustomersList(g).length, 0);
    const totalPeople = leadCount + peopleCount;
    return {
      totalPeople,
      groupCount: nGroups,
      maxPeople: selectedExcursionTemplate?.max_people_per_excursion,
      maxGroups: selectedExcursionTemplate?.max_groups_per_excursion,
      show:
        nGroups > 0 ||
        selectedExcursionTemplate?.max_people_per_excursion != null ||
        selectedExcursionTemplate?.max_groups_per_excursion != null,
    };
  }, [groups, selectedExcursionTemplate]);

  const canAddMoreGroups = React.useMemo(() => {
    const maxGroups = selectedExcursionTemplate?.max_groups_per_excursion;
    if (maxGroups == null) return true;
    return groups.length < maxGroups;
  }, [groups.length, selectedExcursionTemplate]);

  const getGroupParamsTemplateForIndex = React.useCallback(
    (index: number): Record<string, string> => {
      const g = groups[index];
      const templateId = g?.paramsTemplateId ?? defaultGroupTemplateId;
      const t = templateId != null ? groupTemplatesList.find((e) => e.id === templateId) : null;
      return filterTemplateToRecord(
        (t ?? groupTemplatesList[0])?.params_template as Record<string, unknown>,
      );
    },
    [groups, defaultGroupTemplateId, groupTemplatesList],
  );

  const getGroupLimitsForIndex = React.useCallback(
    (index: number): { maxUsersPerGroup?: number; maxCustomersPerGroup?: number } => {
      const g = groups[index];
      const templateId = g?.paramsTemplateId ?? defaultGroupTemplateId;
      const t =
        templateId != null
          ? groupTemplatesList.find((e) => e.id === templateId)
          : groupTemplatesList[0];
      if (!t) return {};
      return {
        maxUsersPerGroup: t.max_users_per_group,
        maxCustomersPerGroup: t.max_customers_per_group,
      };
    },
    [groups, defaultGroupTemplateId, groupTemplatesList],
  );

  const setGroupTemplateId = React.useCallback(
    (index: number, templateId: number | null) => {
      setGroups((prev) =>
        prev.map((g, i) => {
          if (i !== index) return g;

          const template =
            templateId != null ? groupTemplatesList.find((e) => e.id === templateId) : undefined;
          const groupColor = template?.color?.trim() ? template.color : g.color;

          return {
            ...g,
            paramsTemplateId: templateId ?? undefined,
            params: {},
            color: groupColor,
          };
        }),
      );
    },
    [groupTemplatesList],
  );

  const validateLimits = React.useCallback((): string[] => {
    const errors: string[] = [];
    const nGroups = groups.length;
    const totalPeople = groups.reduce(
      (s, g) => s + (g.users?.length ?? 0) + groupCustomersList(g).length,
      0,
    );

    const maxPeople = selectedExcursionTemplate?.max_people_per_excursion;
    if (maxPeople != null && totalPeople > maxPeople) {
      errors.push(
        `Nombre total de personnes (${totalPeople}) dépasse la limite de la sortie (${maxPeople}).`,
      );
    }
    const maxGroups = selectedExcursionTemplate?.max_groups_per_excursion;
    if (maxGroups != null && nGroups > maxGroups) {
      errors.push(`Nombre de groupes (${nGroups}) dépasse la limite de la sortie (${maxGroups}).`);
    }

    groups.forEach((g, i) => {
      const limits = getGroupLimitsForIndex(i);
      const leadCount = g.users?.length ?? 0;
      const peopleCount = groupCustomersList(g).length;
      if (limits.maxUsersPerGroup != null && leadCount > limits.maxUsersPerGroup) {
        errors.push(
          `Groupe ${i + 1} : nombre d'encadrants (${leadCount}) dépasse la limite (${limits.maxUsersPerGroup}).`,
        );
      }
      if (limits.maxCustomersPerGroup != null && peopleCount > limits.maxCustomersPerGroup) {
        errors.push(
          `Groupe ${i + 1} : nombre de clients (${peopleCount}) dépasse la limite (${limits.maxCustomersPerGroup}).`,
        );
      }
    });

    return errors;
  }, [groups, selectedExcursionTemplate, getGroupLimitsForIndex]);

  const slotMonthKey = React.useMemo(() => toDateInputValue(date).slice(0, 7), [date]);

  const excursionSlotBounds = React.useMemo(
    () => parseExcursionSlotBounds(toDateInputValue(date), startTime, endTime),
    [date, startTime, endTime],
  );

  React.useEffect(() => {
    if (!open || isReadonly || user?.company_id == null) {
      setSlotOverlapLeaves([]);
      setSlotOverlapBookings([]);

      return;
    }

    const range = monthRangeContainingYmd(toDateInputValue(date));
    if (!range) {
      setSlotOverlapLeaves([]);
      setSlotOverlapBookings([]);

      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const lrPromise =
          getLeaveRequests != null
            ? getLeaveRequests({ From: range.from, To: range.to })
            : Promise.resolve(undefined);
        const [lr, bk] = await Promise.all([
          lrPromise,
          listExcursionBookings({ from: range.from, to: range.to }).catch(() => []),
        ]);
        if (cancelled) return;
        setSlotOverlapLeaves(lr != null ? (extractDataFromResponse<LeaveRequest>(lr) ?? []) : []);
        setSlotOverlapBookings(Array.isArray(bk) ? bk : []);
      } catch {
        if (!cancelled) {
          setSlotOverlapLeaves([]);
          setSlotOverlapBookings([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    isReadonly,
    slotMonthKey,
    user?.company_id,
    getLeaveRequests,
    listExcursionBookings,
    date,
  ]);

  const searchUsersForParams = React.useCallback(
    async (query: string): Promise<SearchResultItem[]> => {
      const us = await getUsers();
      if (!us || query.length < 2) return [];
      const q = query.toLowerCase();
      const companyId = user?.company_id ?? null;
      const ymd = toDateInputValue(date);
      const slot = excursionSlotBounds;
      const exId = initialExcursion?.id ?? null;
      return us
        .filter((u) =>
          (
            firstLastQualificationLabel(
              u.user.first_name,
              u.user.last_name,
              u.user.last_qualification,
            ) || `Utilisateur #${u.user.id}`
          )
            .toLowerCase()
            .includes(q),
        )
        .map((u) => {
          const uid = u.user.id;
          const assignedElsewhere = groups.some((grp) =>
            (grp.users ?? []).some((x) => x.id === uid),
          );
          const kind =
            companyId != null
              ? findUserSlotConflict({
                  userId: uid,
                  companyId,
                  excursionYmd: ymd,
                  slot,
                  leaves: slotOverlapLeaves,
                  bookings: slotOverlapBookings,
                  excludeExcursionId: exId,
                  assignedElsewhereInForm: assignedElsewhere,
                })
              : assignedElsewhere
                ? "same_excursion_group"
                : null;
          return {
            id: uid,
            label:
              firstLastQualificationLabel(
                u.user.first_name,
                u.user.last_name,
                u.user.last_qualification,
              ) || `Utilisateur #${uid}`,
            disabled: kind != null,
            conflictHint: kind != null ? slotConflictHint(kind) : undefined,
          };
        });
    },
    [
      getUsers,
      user?.company_id,
      date,
      excursionSlotBounds,
      groups,
      slotOverlapLeaves,
      slotOverlapBookings,
      initialExcursion?.id,
    ],
  );

  const searchGuidesForParams = React.useCallback(
    async (query: string): Promise<SearchResultItem[]> => {
      const us = await getUsers();
      if (!us || query.length < 2) return [];
      const q = query.toLowerCase();
      const companyId = user?.company_id ?? null;
      const ymd = toDateInputValue(date);
      const slot = excursionSlotBounds;
      const exId = initialExcursion?.id ?? null;
      return us
        .filter(isUserGuideForExcursionParams)
        .filter((u) =>
          (
            firstLastQualificationLabel(
              u.user.first_name,
              u.user.last_name,
              u.user.last_qualification,
            ) || `Utilisateur #${u.user.id}`
          )
            .toLowerCase()
            .includes(q),
        )
        .map((u) => {
          const uid = u.user.id;
          const assignedElsewhere = groups.some((grp) =>
            (grp.users ?? []).some((x) => x.id === uid),
          );
          const kind =
            companyId != null
              ? findUserSlotConflict({
                  userId: uid,
                  companyId,
                  excursionYmd: ymd,
                  slot,
                  leaves: slotOverlapLeaves,
                  bookings: slotOverlapBookings,
                  excludeExcursionId: exId,
                  assignedElsewhereInForm: assignedElsewhere,
                })
              : assignedElsewhere
                ? "same_excursion_group"
                : null;
          return {
            id: uid,
            label:
              firstLastQualificationLabel(
                u.user.first_name,
                u.user.last_name,
                u.user.last_qualification,
              ) || `Utilisateur #${uid}`,
            disabled: kind != null,
            conflictHint: kind != null ? slotConflictHint(kind) : undefined,
          };
        });
    },
    [
      getUsers,
      user?.company_id,
      date,
      excursionSlotBounds,
      groups,
      slotOverlapLeaves,
      slotOverlapBookings,
      initialExcursion?.id,
    ],
  );

  const searchCustomersForParams = React.useCallback(
    async (query: string): Promise<SearchResultItem[]> => {
      if (query.length < 2) return [];
      const results = await searchCustomers({ Query: query, SortOrder: "asc" });
      return (results ?? [])
        .filter((c): c is ZekayCustomer & { Id: number } => c.Id != null)
        .map((c) => ({
          id: c.Id,
          label:
            firstLastQualificationLabel(c.FirstName, c.LastName, c.LastQualification) || `#${c.Id}`,
        }));
    },
    [searchCustomers],
  );

  const customerIdsForLabels = React.useMemo(() => {
    const ids: number[] = [];
    (initialExcursion?.groups ?? []).forEach((g) => {
      (g.excursion_customers ?? []).forEach((c) => ids.push(c.id));
    });
    groups.forEach((g) => {
      groupCustomersList(g).forEach((c) => ids.push(c.id));
    });
    return Array.from(new Set(ids));
  }, [initialExcursion?.groups, groups]);

  React.useEffect(() => {
    if (preloadedCustomers?.length) {
      setCustomersForLabels(preloadedCustomers);
      return;
    }
    if (!open || customerIdsForLabels.length === 0) {
      if (open && customerIdsForLabels.length === 0) setCustomersForLabels([]);
      return;
    }
    getCustomersBatch(customerIdsForLabels).then((data) => {
      setCustomersForLabels(Array.isArray(data) ? data : []);
    });
  }, [open, customerIdsForLabels, getCustomersBatch, preloadedCustomers]);

  React.useEffect(() => {
    if (!clientSearch || clientSearch.query.length < 3) {
      if (clientSearch) setClientSearch((prev) => (prev ? { ...prev, results: [] } : null));
      return;
    }
    const t = setTimeout(() => {
      searchCustomers({ Query: clientSearch.query, SortOrder: "asc" })
        .then((results) =>
          setClientSearch((prev) => (prev ? { ...prev, results: results ?? [] } : null)),
        )
        .catch(() => setClientSearch((prev) => (prev ? { ...prev, results: [] } : null)));
    }, 500);
    return () => clearTimeout(t);
  }, [clientSearch?.query, clientSearch?.groupIndex, searchCustomers]);

  const bookingStatusMap = React.useMemo(() => {
    const map: Record<number, "pending" | "validated"> = {};
    for (const b of bookings) {
      if (b.user_id == null) continue;
      if (b.status === "pending" || b.status === "validated") {
        map[b.user_id] = b.status;
      }
    }
    return map;
  }, [bookings]);

  const rejectedUserIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const b of bookings) {
      if (b.status === "rejected" && b.user_id != null) ids.add(b.user_id);
    }
    return ids;
  }, [bookings]);

  const filteredUsers = React.useMemo(() => {
    if (!userSearch) return [];
    const raw = userSearch.query.trim();
    const q = raw.toLowerCase();
    const groupIndex = userSearch.groupIndex;
    const companyId = user?.company_id ?? null;
    const ymd = toDateInputValue(date);
    const slot = excursionSlotBounds;
    const exId = initialExcursion?.id ?? null;
    return users
      .filter((u) => {
        if (rejectedUserIds.has(u.id)) return false;
        if (raw.length === 0) return true;
        const searchable = u.qualification ? `${u.name} ${u.qualification}` : u.name;
        return searchable.toLowerCase().includes(q);
      })
      .map((u) => {
        const assignedElsewhere = groups.some(
          (grp, idx) => idx !== groupIndex && (grp.users ?? []).some((x) => x.id === u.id),
        );
        const kind =
          companyId != null
            ? findUserSlotConflict({
                userId: u.id,
                companyId,
                excursionYmd: ymd,
                slot,
                leaves: slotOverlapLeaves,
                bookings: slotOverlapBookings,
                excludeExcursionId: exId,
                assignedElsewhereInForm: assignedElsewhere,
              })
            : assignedElsewhere
              ? "same_excursion_group"
              : null;
        return {
          ...u,
          slotUnavailable: kind != null,
          slotConflictHint: kind != null ? slotConflictHint(kind) : undefined,
        };
      });
  }, [
    userSearch,
    users,
    rejectedUserIds,
    groups,
    user?.company_id,
    date,
    excursionSlotBounds,
    slotOverlapLeaves,
    slotOverlapBookings,
    initialExcursion?.id,
  ]);

  React.useEffect(() => {
    if (!open) return;
    setGroups((prev) => {
      const hasCustomerIds = prev.some((g) => g.customer_id != null && !g.customer_label);
      const hasCustomersToFill = prev.some((g) =>
        groupCustomersList(g).some((c) => !c.first_name && !c.last_name && c.id),
      );
      const hasUsersToFill = prev.some((g) => g.users?.some((u) => !u.first_name && !u.last_name));
      if (!hasCustomerIds && !hasCustomersToFill && !hasUsersToFill) return prev;
      const result = prev.map((g) => {
        let next = g;
        if (g.customer_id != null && !g.customer_label) {
          const c = customersForLabels.find((c) => c.Id === g.customer_id);
          if (c) {
            next = {
              ...next,
              customer_label: [c.FirstName, c.LastName].filter(Boolean).join(" ") || `#${c.Id}`,
              customer_last_name: c.LastName?.trim(),
              customer_first_name: c.FirstName?.trim(),
              customer_qualification: c.LastQualification?.trim(),
            };
          }
        }
        if (next.customers?.length) {
          const filled = next.customers.map((client) => {
            const c = customersForLabels.find((row) => row.Id === client.id);
            const trainingFromBatch =
              c != null ? formatInProgressTrainingsSessionsTooltip(c.Trainings) : undefined;
            const fromBatch =
              c != null
                ? {
                    first_name: client.first_name ?? c.FirstName?.trim(),
                    last_name: client.last_name ?? c.LastName?.trim(),
                    qualification: client.qualification ?? c.LastQualification?.trim(),
                    emoji: normalizeCustomerEmoji(c.Emoji ?? undefined) ?? client.emoji ?? null,
                  }
                : null;
            const trainingPatch =
              c != null ? { inProgressTrainingsSessionsTooltip: trainingFromBatch } : {};
            if (!fromBatch) return { ...client, ...trainingPatch };
            if (client.first_name || client.last_name) {
              return {
                ...client,
                ...trainingPatch,
                emoji: fromBatch.emoji ?? client.emoji ?? null,
              };
            }
            return {
              ...client,
              ...trainingPatch,
              first_name: fromBatch.first_name,
              last_name: fromBatch.last_name,
              qualification: fromBatch.qualification,
              emoji: fromBatch.emoji,
            };
          });
          next = { ...next, customers: filled };
          const first = filled[0];
          if (first && !next.customer_label) {
            next = {
              ...next,
              customer_label: personName(first.first_name, first.last_name, first.id),
              customer_first_name: first.first_name,
              customer_last_name: first.last_name,
              customer_qualification: first.qualification,
            };
          }
        }
        if (next.users?.length) {
          const filled = next.users.map((u) => {
            const found = users.find((us) => us.id === u.id);
            if (!found) return u;
            return {
              ...u,
              first_name: found.first_name,
              last_name: found.last_name,
              qualification: found.qualification ?? u.qualification,
            };
          });
          next = { ...next, users: filled };
        }
        return next;
      });
      return result;
    });
  }, [open, customersForLabels, users]);

  const buildExcursionRequestPayload = React.useCallback(
    (statusOverride?: ExcursionStatus): ExcursionRequest | null => {
      if (!name.trim()) return null;
      const payloadStatus = statusOverride ?? status ?? ExcursionStatusPending;
      const cancellationReasonPayload =
        payloadStatus === ExcursionStatusCancelled
          ? cancellationReason.trim().slice(0, maxExcursionCancellationReasonLen)
          : "";

      return {
        date: toDateInputValue(date),
        start_time: startTime.trim() || null,
        end_time: endTime.trim() || null,
        name: name.trim(),
        color: excursionColor?.trim() || undefined,
        status: payloadStatus,
        cancellation_reason: cancellationReasonPayload,
        params_template:
          selectedExcursionTemplateId != null ? { id: selectedExcursionTemplateId } : null,
        params: (() => {
          const sanitized = sanitizeExcursionParamsAgainstTemplate(paramsTemplate, params);
          const cleaned = stripPicklistSearchMetadataForPersist(paramsTemplate, sanitized);
          const filtered: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(cleaned)) {
            if (v !== undefined && v !== null && v !== "") filtered[k] = v;
          }
          return Object.keys(filtered).length > 0 ? filtered : undefined;
        })(),
        groups: groups.map((g, groupIndex): ExcursionGroupRequest => {
          const groupTpl = getGroupParamsTemplateForIndex(groupIndex);
          const groupParamsSan = sanitizeExcursionParamsAgainstTemplate(
            groupTpl,
            (g.params as Record<string, unknown>) ?? {},
          );
          const groupParams = stripPicklistSearchMetadataForPersist(groupTpl, groupParamsSan);
          const filteredParams: Record<string, unknown> = {};
          const skipKeys = ["id", "group_param_id", "excursion_group_param_id"];
          for (const [k, v] of Object.entries(groupParams)) {
            if (skipKeys.includes(k)) continue;
            if (v !== undefined && v !== null && v !== "") filteredParams[k] = v;
          }
          const groupColor = g.color && String(g.color).trim() ? g.color : GROUP_DEFAULT_COLOR;
          const excursion_customers: ExcursionCustomer[] = groupCustomersList(g).map(
            (c): ExcursionCustomer => {
              const hex = (c.color && String(c.color).trim() ? c.color : null) ?? groupColor;

              return { id: c.id, color: hex, comments: c.comments };
            },
          );
          const excursion_users: ExcursionUser[] = (g.users ?? []).map((u) => ({
            id: u.id,
            color: (u.color && String(u.color).trim() ? u.color : null) ?? groupColor,
            comments: u.comments,
          }));
          const req: ExcursionGroupRequest = {
            excursion_customers,
            excursion_users,
            params: Object.keys(filteredParams).length > 0 ? filteredParams : undefined,
            color: groupColor,
          };
          if (g.id != null) req.id = g.id;
          const templateId = g.paramsTemplateId ?? defaultGroupTemplateId;
          if (templateId != null) req.excursion_group_param_id = templateId;
          return req;
        }),
      };
    },
    [
      name,
      date,
      startTime,
      endTime,
      excursionColor,
      status,
      cancellationReason,
      params,
      paramsTemplate,
      groups,
      getGroupParamsTemplateForIndex,
      defaultGroupTemplateId,
      selectedExcursionTemplateId,
    ],
  );

  const closeExcursionCreateProfile = React.useCallback(() => {
    setCreateCustomerModalGroupIndex(null);
    setCreateCustomerDraft(undefined);
    setClientSearch(null);
  }, []);

  const noopCustomerUpdated = React.useCallback(
    (_: React.SetStateAction<ZekayCustomer | undefined>) => {},
    [],
  );

  const excursionCreateProfileSetter = React.useCallback(
    (value: React.SetStateAction<boolean>) => {
      const next = typeof value === "function" ? value(false) : value;
      if (!next) closeExcursionCreateProfile();
    },
    [closeExcursionCreateProfile],
  );

  const handleCustomerOverviewUpdated = React.useCallback((updated: ZekayCustomer) => {
    if (updated.Id == null) return;
    setCustomersForLabels((prev) => {
      const idx = prev.findIndex((c) => c.Id === updated.Id);
      if (idx < 0) return [...prev, updated];
      const row = [...prev];
      row[idx] = { ...row[idx], ...updated };
      return row;
    });
    setGroups((prev) =>
      prev.map((g) => {
        const list = groupCustomersList(g);
        if (!list.some((c) => c.id === updated.Id)) {
          return g;
        }
        const nextClients = list.map((c) =>
          c.id === updated.Id
            ? {
                ...c,
                first_name: updated.FirstName?.trim(),
                last_name: updated.LastName?.trim(),
                qualification: updated.LastQualification?.trim(),
                emoji: normalizeCustomerEmoji(updated.Emoji ?? undefined) ?? c.emoji ?? null,
              }
            : c,
        );
        const first = nextClients[0];
        return {
          ...g,
          customers: nextClients,
          customer_id: first?.id ?? g.customer_id ?? null,
          customer_label: first
            ? personName(first.first_name, first.last_name, first.id)
            : g.customer_label,
          customer_first_name: first?.first_name,
          customer_last_name: first?.last_name,
          customer_qualification: first?.qualification,
        };
      }),
    );
  }, []);

  const dismissNestedExcursionModals = React.useCallback(() => {
    setValidateCountersOpen(false);
    setCommentsModalGroupIndex(null);
    closeExcursionCreateProfile();
    setUserSearch(null);
    setCustomerOverviewCustomerId(null);
  }, [closeExcursionCreateProfile]);

  const handleSubmit = async () => {
    if (isReadonly || !onSave || !name.trim()) return;
    if (status === ExcursionStatusPending) {
      const limitErrors = validateLimits();
      if (limitErrors.length > 0) {
        showAlert(limitErrors.join(" "), "warning");

        return;
      }
    }

    const payload = buildExcursionRequestPayload();
    if (!payload) return;
    setLoading(true);
    try {
      await onSave(payload);
      dismissNestedExcursionModals();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCountersSuccess = React.useCallback(async () => {
    setStatus(ExcursionStatusDone);
    setValidateCountersOpen(false);
    if (!preloadedCustomers?.length && customerIdsForLabels.length > 0) {
      getCustomersBatch(customerIdsForLabels).then((data) => {
        setCustomersForLabels(Array.isArray(data) ? data : []);
      });
    }
    if (!isReadonly && onSave && initialExcursion?.id && onPersistAfterCounters) {
      const limitErrors = validateLimits();
      if (limitErrors.length > 0) {
        showAlert(limitErrors.join(" "), "warning");
        return;
      }
      const payload = buildExcursionRequestPayload(ExcursionStatusDone);
      if (payload) {
        setLoading(true);
        try {
          await onPersistAfterCounters(payload);
        } finally {
          setLoading(false);
        }
      }
      onValidateCountersApplied?.();
      dismissNestedExcursionModals();
      onClose();
      return;
    }
    onValidateCountersApplied?.();
    dismissNestedExcursionModals();
    onClose();
  }, [
    isReadonly,
    onSave,
    initialExcursion?.id,
    onPersistAfterCounters,
    preloadedCustomers?.length,
    customerIdsForLabels,
    getCustomersBatch,
    validateLimits,
    buildExcursionRequestPayload,
    onValidateCountersApplied,
    dismissNestedExcursionModals,
    onClose,
    showAlert,
  ]);

  const handleDeleteClick = async () => {
    if (!onDelete) return;
    const ok = await showConfirmation(
      "Supprimer la sortie",
      "Confirmer la suppression de cette sortie ?",
    );
    if (!ok) return;
    setLoading(true);
    try {
      await onDelete();
      dismissNestedExcursionModals();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const addGroup = () => {
    const maxGroups = selectedExcursionTemplate?.max_groups_per_excursion;
    if (maxGroups != null && groups.length >= maxGroups) {
      showAlert(`Nombre maximum de groupes (${maxGroups}) atteint pour cette sortie.`, "warning");
      return;
    }

    const template =
      defaultGroupTemplateId != null
        ? groupTemplatesList.find((t) => t.id === defaultGroupTemplateId)
        : groupTemplatesList[0];
    const groupColor = template?.color?.trim() ? template.color : GROUP_DEFAULT_COLOR;
    const localKey = nextGroupLocalKey();

    setGroups((prev) => [
      ...prev,
      {
        local_key: localKey,
        users: [],
        color: groupColor,
        customers: [],
        params: {},
        paramsTemplateId: defaultGroupTemplateId,
      },
    ]);
    setScrollToGroupLocalKey(localKey);
  };
  const removeGroup = (index: number) => setGroups((prev) => prev.filter((_, i) => i !== index));
  const handleRemoveGroup = React.useCallback(
    (index: number) => async () => {
      const ok = await showConfirmation("Supprimer le groupe", "Retirer ce groupe de la sortie ?");
      if (ok) removeGroup(index);
    },
    [showConfirmation, removeGroup],
  );
  const setGroupCustomer = (index: number, customer: ZekayCustomer) => {
    if (customer.Id == null) return;

    const alreadyInAnotherGroup = groups.some(
      (grp, grpIdx) =>
        grpIdx !== index && groupCustomersList(grp).some((c) => c.id === customer.Id),
    );
    if (alreadyInAnotherGroup) {
      showAlert("Ce client est déjà affecté à un autre groupe pour cette sortie.", "warning");
      return;
    }

    const g = groups[index];
    const existing = g ? groupCustomersList(g) : [];
    if (existing.some((c) => c.id === customer.Id)) return;
    const limits = getGroupLimitsForIndex(index);
    if (limits.maxCustomersPerGroup != null && existing.length >= limits.maxCustomersPerGroup) {
      showAlert(
        `Nombre maximum de clients (${limits.maxCustomersPerGroup}) atteint pour ce groupe.`,
        "warning",
      );
      return;
    }
    const maxPeople = selectedExcursionTemplate?.max_people_per_excursion;
    if (maxPeople != null && modalTotals.totalPeople >= maxPeople) {
      showAlert(`Nombre maximum de personnes (${maxPeople}) atteint pour cette sortie.`, "warning");
      return;
    }
    setGroups((prev) =>
      prev.map((grp, i) => {
        if (i !== index) return grp;
        const list = groupCustomersList(grp);
        const groupColorForNew =
          grp.color && String(grp.color).trim() ? grp.color : GROUP_DEFAULT_COLOR;
        const client: ExcursionGroupCardClient = {
          id: customer.Id as number,
          first_name: customer.FirstName?.trim(),
          last_name: customer.LastName?.trim(),
          qualification: customer.LastQualification?.trim(),
          color: groupColorForNew,
          emoji: normalizeCustomerEmoji(customer.Emoji ?? undefined) ?? null,
          inProgressTrainingsSessionsTooltip: formatInProgressTrainingsSessionsTooltip(
            customer.Trainings,
          ),
        };
        const nextCustomers = [...list, client];
        const first = nextCustomers[0];
        return {
          ...grp,
          customers: nextCustomers,
          customer_id: first?.id ?? null,
          customer_label: personName(first?.first_name, first?.last_name, first?.id),
          customer_first_name: first?.first_name,
          customer_last_name: first?.last_name,
          customer_qualification: first?.qualification,
        };
      }),
    );
  };

  const setGroupRemoveClient = (index: number, clientIndex: number) =>
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== index) return g;
        const list = groupCustomersList(g);
        const next = list.filter((_, j) => j !== clientIndex);
        const first = next[0];
        return {
          ...g,
          customers: next,
          customer_id: first?.id ?? null,
          customer_label: personName(first?.first_name, first?.last_name, first?.id),
          customer_first_name: first?.first_name,
          customer_last_name: first?.last_name,
          customer_qualification: first?.qualification,
        };
      }),
    );
  const setGroupAddUser = (
    index: number,
    member: {
      id: number;
      name: string;
      first_name?: string;
      last_name?: string;
      qualification?: string;
    },
  ) => {
    const limits = getGroupLimitsForIndex(index);
    if (limits.maxUsersPerGroup != null && limits.maxUsersPerGroup <= 0) {
      showAlert("Ce groupe n'accepte pas d'encadrant (limite à 0).", "warning");
      return;
    }
    const g = groups[index];
    const existing = g?.users ?? [];
    if (existing.some((u) => u.id === member.id)) return;
    if (limits.maxUsersPerGroup != null && existing.length >= limits.maxUsersPerGroup) {
      showAlert(
        `Nombre maximum d'encadrants (${limits.maxUsersPerGroup}) atteint pour ce groupe.`,
        "warning",
      );
      return;
    }
    const maxPeople = selectedExcursionTemplate?.max_people_per_excursion;
    if (maxPeople != null && modalTotals.totalPeople >= maxPeople) {
      showAlert(`Nombre maximum de personnes (${maxPeople}) atteint pour cette sortie.`, "warning");
      return;
    }
    const companyId = user?.company_id ?? null;
    const ymd = toDateInputValue(date);
    const assignedElsewhere = groups.some(
      (grp, idx) => idx !== index && (grp.users ?? []).some((x) => x.id === member.id),
    );
    const conflictKind =
      companyId != null
        ? findUserSlotConflict({
            userId: member.id,
            companyId,
            excursionYmd: ymd,
            slot: excursionSlotBounds,
            leaves: slotOverlapLeaves,
            bookings: slotOverlapBookings,
            excludeExcursionId: initialExcursion?.id ?? null,
            assignedElsewhereInForm: assignedElsewhere,
          })
        : assignedElsewhere
          ? "same_excursion_group"
          : null;
    if (conflictKind != null) {
      showAlert(slotConflictHint(conflictKind), "warning");
      return;
    }
    const groupColor = g?.color && String(g.color).trim() ? g.color : GROUP_DEFAULT_COLOR;
    setGroups((prev) =>
      prev.map((grp, i) =>
        i === index
          ? {
              ...grp,
              users: [
                ...(grp.users ?? []),
                {
                  id: member.id,
                  first_name: member.first_name,
                  last_name: member.last_name,
                  qualification: member.qualification,
                  color: groupColor,
                  comments: [],
                },
              ],
            }
          : grp,
      ),
    );
  };
  const setGroupRemoveUser = (index: number, userIndex: number) =>
    setGroups((prev) =>
      prev.map((g, i) =>
        i === index ? { ...g, users: (g.users ?? []).filter((_, j) => j !== userIndex) } : g,
      ),
    );
  const setGroupColor = (index: number, hex: string) =>
    setGroups((prev) => prev.map((g, i) => (i === index ? { ...g, color: hex } : g)));
  const setGroupUserColor = (index: number, userIndex: number, hex: string) =>
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== index) return g;
        const list = g.users ?? [];
        const next = list.map((u, j) => (j === userIndex ? { ...u, color: hex } : u));
        return { ...g, users: next };
      }),
    );
  const setGroupClientColor = (index: number, clientIndex: number, hex: string) =>
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== index) return g;
        const list = groupCustomersList(g);
        const next = list.map((c, j) => (j === clientIndex ? { ...c, color: hex } : c));
        return { ...g, customers: next };
      }),
    );
  const setGroupUserComments = (index: number, userIndex: number, comments: MultiComment[]) =>
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== index) return g;
        const list = g.users ?? [];
        const next = list.map((u, j) => (j === userIndex ? { ...u, comments } : u));
        return { ...g, users: next };
      }),
    );
  const setGroupClientComments = (index: number, clientIndex: number, comments: MultiComment[]) =>
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i !== index) return g;
        const list = groupCustomersList(g);
        const next = list.map((c, j) => (j === clientIndex ? { ...c, comments } : c));
        return { ...g, customers: next };
      }),
    );
  const setGroupComments = (index: number, comments: MultiComment[] | undefined) =>
    setGroups((prev) => prev.map((g, i) => (i === index ? { ...g, comments: comments ?? [] } : g)));
  const setGroupParams = (index: number, params: Record<string, unknown>) =>
    setGroups((prev) => prev.map((g, i) => (i === index ? { ...g, params } : g)));

  const openClientSearch = (groupIndex: number) =>
    setClientSearch({ groupIndex, query: "", results: [] });
  const openUserSearch = (groupIndex: number) => setUserSearch({ groupIndex, query: "" });

  const handleClientDropOnGroup = (
    targetGroupIndex: number,
    sourceGroupIndex: number,
    clientIndex: number,
    customerId: number,
    _customerLabel: string,
    customerDetails?: { lastName?: string; firstName?: string; qualification?: string },
    customerColor?: string | null,
    customerComments?: MultiComment[],
    customerEmoji?: string | null,
  ) => {
    if (targetGroupIndex === sourceGroupIndex || !canEditExcursionDetails) return;

    const alreadyInAnotherGroup = groups.some(
      (grp, grpIdx) =>
        grpIdx !== sourceGroupIndex &&
        grpIdx !== targetGroupIndex &&
        groupCustomersList(grp).some((c) => c.id === customerId),
    );
    if (alreadyInAnotherGroup) {
      showAlert("Ce client est déjà affecté à un autre groupe pour cette sortie.", "warning");
      return;
    }

    const targetGroup = groups[targetGroupIndex];
    const targetList = targetGroup ? groupCustomersList(targetGroup) : [];
    if (targetList.some((c) => c.id === customerId)) return;

    const limits = getGroupLimitsForIndex(targetGroupIndex);
    if (limits.maxCustomersPerGroup != null && targetList.length >= limits.maxCustomersPerGroup) {
      showAlert(
        `Nombre maximum de clients (${limits.maxCustomersPerGroup}) atteint pour ce groupe.`,
        "warning",
      );
      return;
    }
    const groupColor =
      targetGroup?.color && String(targetGroup.color).trim()
        ? targetGroup.color
        : GROUP_DEFAULT_COLOR;
    const clientColor = customerColor && String(customerColor).trim() ? customerColor : groupColor;
    const batch = customersForLabels.find((row) => row.Id === customerId);
    const normalizedEmoji =
      normalizeCustomerEmoji(batch?.Emoji ?? undefined) ??
      normalizeCustomerEmoji(customerEmoji ?? undefined) ??
      null;
    const client: ExcursionGroupCardClient = {
      id: customerId,
      first_name: customerDetails?.firstName,
      last_name: customerDetails?.lastName,
      qualification: customerDetails?.qualification,
      color: clientColor,
      comments: customerComments,
      emoji: normalizedEmoji,
      inProgressTrainingsSessionsTooltip: formatInProgressTrainingsSessionsTooltip(
        batch?.Trainings,
      ),
    };
    setGroups((prev) =>
      prev.map((g, i) => {
        if (i === sourceGroupIndex) {
          const list = groupCustomersList(g);
          const next = list.filter((_, j) => j !== clientIndex);
          const first = next[0];
          return {
            ...g,
            customers: next,
            customer_id: first?.id ?? null,
            customer_label: personName(first?.first_name, first?.last_name, first?.id),
            customer_first_name: first?.first_name,
            customer_last_name: first?.last_name,
            customer_qualification: first?.qualification,
          };
        }
        if (i === targetGroupIndex) {
          const list = groupCustomersList(g);
          const next = [...list, client];
          const first = next[0];
          return {
            ...g,
            customers: next,
            customer_id: first?.id ?? null,
            customer_label: personName(first?.first_name, first?.last_name, first?.id),
            customer_first_name: first?.first_name,
            customer_last_name: first?.last_name,
            customer_qualification: first?.qualification,
          };
        }
        return g;
      }),
    );
  };

  const handleExportPdf = React.useCallback(() => {
    const pdfGroups: PdfGroupData[] = groups.map((g, idx) => ({
      index: idx,
      color: g.color,
      users: (g.users ?? []).map((u) => ({
        label:
          firstLastQualificationLabel(u.first_name, u.last_name, u.qualification) || `#${u.id}`,
        color: u.color,
        pdfComment: firstImportantComment(u.comments)?.Comment?.trim() || undefined,
      })),
      clients: groupCustomersList(g).map((c) => ({
        label:
          firstLastQualificationLabel(c.first_name, c.last_name, c.qualification) || `#${c.id}`,
        color: c.color,
        pdfComment: firstImportantComment(c.comments)?.Comment?.trim() || undefined,
      })),
      params: g.params ?? {},
      paramsTemplate: getGroupParamsTemplateForIndex(idx),
      comments: [...(g.comments ?? [])],
    }));
    const pdfData: PdfExcursionData = {
      name,
      date,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      status,
      color: excursionColor ?? undefined,
      params,
      paramsTemplate,
      groups: pdfGroups,
      companyName:
        userCompanies.find((c) => c.company.id === user?.company_id)?.company.name || undefined,
    };
    generateExcursionPdf(pdfData);
  }, [
    name,
    date,
    startTime,
    endTime,
    status,
    excursionColor,
    params,
    paramsTemplate,
    groups,
    userCompanies,
    user?.company_id,
  ]);

  const isEditing = Boolean(initialExcursion);
  const excursionPersistedFrozen =
    initialExcursion?.id != null &&
    (initialExcursion.status === ExcursionStatusDone ||
      initialExcursion.status === ExcursionStatusCancelled);
  const cancellingTransition =
    status === ExcursionStatusCancelled &&
    (initialExcursion == null || initialExcursion.status === ExcursionStatusPending);
  const canEditExcursionDetails =
    canEdit && status === ExcursionStatusPending && !excursionPersistedFrozen;
  const canSaveExcursion =
    !isReadonly &&
    canEdit &&
    !excursionPersistedFrozen &&
    (status === ExcursionStatusPending || cancellingTransition);
  const canEditExcursionStatus =
    !isReadonly &&
    canEdit &&
    !excursionPersistedFrozen &&
    (initialExcursion == null || initialExcursion.status === ExcursionStatusPending);
  const modalTitle = (() => {
    if (isReadonly || !canEdit) return "Détail de la sortie";
    if (!isEditing) return "Nouvelle sortie";
    if (excursionPersistedFrozen) return "Détail de la sortie";
    if (canEditExcursionDetails) return "Modifier la sortie";
    if (canSaveExcursion && status === ExcursionStatusCancelled) return "Sortie annulée";

    return "Détail de la sortie";
  })();
  const commentsForModal =
    commentsModalGroupIndex != null ? groups[commentsModalGroupIndex]?.comments : undefined;
  const setCommentsForModal = React.useCallback(
    (action: React.SetStateAction<MultiComment[] | undefined>) => {
      if (commentsModalGroupIndex == null) return;
      const next =
        typeof action === "function" ? action(groups[commentsModalGroupIndex]?.comments) : action;
      setGroupComments(commentsModalGroupIndex, next ?? []);
    },
    [commentsModalGroupIndex, groups],
  );
  return {
    open,
    onClose,
    initialExcursion,
    preloadedCustomers,
    preloadedUsers,
    onValidateCountersApplied,
    onPersistAfterCounters,
    isReadonly,
    onSave,
    onDelete,
    initialDate,
    initialStartTime,
    initialEndTime,
    canEdit,
    nextGroupLocalKey,
    searchCustomers,
    getCustomersBatch,
    sharedGroupTemplates,
    sharedExcursionTemplates,
    isDesync,
    getUsers,
    user,
    userCompanies,
    listExcursionParams,
    listExcursionGroupParams,
    updateExcursionGroupComments,
    listExcursionBookings,
    getLeaveRequests,
    showConfirmation,
    showAlert,
    hasPermission,
    customerOverviewModules,
    canOpenCustomerOverview,
    date,
    setDate,
    name,
    setName,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    excursionColor,
    setExcursionColor,
    status,
    setStatus,
    cancellationReason,
    setCancellationReason,
    groups,
    setGroups,
    excursionTemplatesList,
    setExcursionTemplatesList,
    groupTemplatesList,
    setGroupTemplatesList,
    selectedExcursionTemplateId,
    setSelectedExcursionTemplateId,
    params,
    setParams,
    paramsTemplate,
    setParamsTemplate,
    loading,
    setLoading,
    dataLoading,
    setDataLoading,
    users,
    setUsers,
    customersForLabels,
    setCustomersForLabels,
    clientSearch,
    setClientSearch,
    userSearch,
    setUserSearch,
    commentsModalGroupIndex,
    setCommentsModalGroupIndex,
    createCustomerModalGroupIndex,
    setCreateCustomerModalGroupIndex,
    createCustomerDraft,
    setCreateCustomerDraft,
    bookings,
    setBookings,
    slotOverlapLeaves,
    setSlotOverlapLeaves,
    slotOverlapBookings,
    setSlotOverlapBookings,
    scrollToGroupLocalKey,
    setScrollToGroupLocalKey,
    validateCountersOpen,
    setValidateCountersOpen,
    customerOverviewCustomerId,
    setCustomerOverviewCustomerId,
    counterValidationGroupSlices,
    counterValidationHasClients,
    handleHeaderStatusChange,
    currentUserName,
    handleExcursionTemplateChange,
    defaultGroupTemplateId,
    selectedExcursionTemplate,
    modalTotals,
    getGroupParamsTemplateForIndex,
    getGroupLimitsForIndex,
    setGroupTemplateId,
    validateLimits,
    slotMonthKey,
    excursionSlotBounds,
    searchUsersForParams,
    searchGuidesForParams,
    searchCustomersForParams,
    customerIdsForLabels,
    bookingStatusMap,
    rejectedUserIds,
    filteredUsers,
    buildExcursionRequestPayload,
    closeExcursionCreateProfile,
    noopCustomerUpdated,
    excursionCreateProfileSetter,
    handleCustomerOverviewUpdated,
    dismissNestedExcursionModals,
    handleSubmit,
    handleValidateCountersSuccess,
    handleDeleteClick,
    addGroup,
    canAddMoreGroups,
    removeGroup,
    handleRemoveGroup,
    setGroupCustomer,
    setGroupRemoveClient,
    setGroupAddUser,
    setGroupRemoveUser,
    setGroupColor,
    setGroupUserColor,
    setGroupClientColor,
    setGroupUserComments,
    setGroupClientComments,
    setGroupComments,
    setGroupParams,
    openClientSearch,
    openUserSearch,
    handleClientDropOnGroup,
    handleExportPdf,
    isEditing,
    canEditExcursionDetails,
    canEditExcursionStatus,
    canSaveExcursion,
    modalTitle,
    commentsForModal,
    setCommentsForModal,
  };
}
